"use client";

import { ClipboardList, Bath, MapPin, FileText } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Benefit = {
  icon: "planning" | "updates" | "local" | "process";
  label: string;
  caption: string;
};
const benefits: Benefit[] = [{
  icon: "planning",
  label: "Bathroom Remodel Planning",
  caption: "Get guidance from a local bathroom remodel professional for your Lakeland project, including layout ideas, materials, updates, and next steps."
}, {
  icon: "updates",
  label: "Shower, Tub, Tile, and Vanity Updates",
  caption: "Request help for common bathroom updates like showers, tubs, tile, vanities, flooring, fixtures, and layout changes."
}, {
  icon: "local",
  label: "Local Lakeland and Polk County Focus",
  caption: "Bathroom remodel quote requests are focused on Lakeland, FL and nearby Polk County areas."
}, {
  icon: "process",
  label: "Easy Quote Request Process",
  caption: "Use the form or call button to request a bathroom remodel quote from a qualified local remodeling business."
}];
function BenefitIcon({
  name
}: {
  name: Benefit["icon"];
}) {
  const cls = "w-7 h-7";
  if (name === "planning") return <ClipboardList className={cls} strokeWidth={1.5} />;
  if (name === "updates") return <Bath className={cls} strokeWidth={1.5} />;
  if (name === "local") return <MapPin className={cls} strokeWidth={1.5} />;
  return <FileText className={cls} strokeWidth={1.5} />;
}
export default function Impact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="14091" id="local-bathroom-remodeling-help" className="dark bg-background text-foreground py-24 md:py-32 px-5 md:px-8">
      <div ref={ref} className="max-w-[1280px] mx-auto">
        <div className="max-w-[720px] mx-auto text-center" style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 720ms cubic-bezier(0.22,0.61,0.36,1), transform 720ms cubic-bezier(0.22,0.61,0.36,1)"
      }}>
          <p className="font-default uppercase font-medium text-[13px]" style={{
          letterSpacing: "0.18em",
          color: "hsl(45 90% 50%)"
        }}>
            Local Expertise
          </p>
          <h2 className="font-default font-semibold mt-5 text-foreground" style={{
          fontSize: "clamp(2.125rem, 4vw, 3.625rem)",
          letterSpacing: "-0.018em",
          lineHeight: 1.06
        }}>Why Lakeland Homeowners Start Here</h2>
          <p className="font-default mt-6 mx-auto" style={{
          fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
          lineHeight: 1.6,
          color: "hsl(220 10% 65%)",
          maxWidth: "560px"
        }}>
            Simple bathroom remodel quote requests for homeowners in Lakeland and Polk County.
          </p>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 relative">
          {benefits.map((benefit, i) => <div data-index={i} key={i} className="relative px-6 py-8 sm:py-6 lg:px-8 lg:py-4 flex flex-col items-start text-left" style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)",
          transition: `opacity 720ms cubic-bezier(0.22,0.61,0.36,1) ${i * 80 + 120}ms, transform 720ms cubic-bezier(0.22,0.61,0.36,1) ${i * 80 + 120}ms`
        }}>
              {i > 0 && <span aria-hidden="true" className="hidden lg:block absolute left-0 top-2 bottom-2 w-px" style={{
            backgroundColor: "hsl(220 10% 25%)"
          }} />}
              {i > 0 && <span aria-hidden="true" className="lg:hidden absolute left-6 right-6 top-0 h-px" style={{
            backgroundColor: "hsl(220 10% 25%)"
          }} />}
              {i % 2 === 1 && <span aria-hidden="true" className="hidden sm:block lg:hidden absolute left-0 top-2 bottom-2 w-px" style={{
            backgroundColor: "hsl(220 10% 25%)"
          }} />}

              <div className="text-primary" style={{
            opacity: isVisible ? 1 : 0,
            transition: `opacity 600ms ease-out ${i * 80}ms`
          }}>
                <BenefitIcon name={benefit.icon} />
              </div>

              <h3 className="font-default mt-8 text-foreground" style={{
            fontSize: "clamp(1.125rem, 1.2vw, 1.3125rem)",
            fontWeight: 600,
            letterSpacing: "-0.005em",
            lineHeight: 1.3
          }}>
                {benefit.label}
              </h3>

              <p className="font-default mt-3" style={{
            fontSize: "0.9375rem",
            lineHeight: 1.55,
            color: "hsl(220 10% 65%)",
            maxWidth: "260px"
          }}>
                {benefit.caption}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}